import * as React from "react";
import {
  Box,
  Checkbox,
  FormControlLabel,
  FormControl,
  FormGroup,
  FormLabel,
  Stack,
  CircularProgress,
  Typography,
} from "@mui/material";
import { BusinessVertical, BusinessVerticalType } from "../../types/vertical";
import LoadingButton from "@mui/lab/LoadingButton";
import {
  useGetSkillsByVerticalQuery,
  useGetSkillsQuery,
} from "../../services/skill.service";
import { Skill, SkillCategory } from "../../types/skill";
import {
  useAddBusinessVerticalMutation,
  useEditBusinessVerticalMutation,
} from "../../services/vertical.service";
import { Controller, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { BaseModal, StyledTextField, Button, SelectBase, SelectOption } from "../../components/global";

const VERTICAL_TYPE_OPTIONS: SelectOption[] = [
  { label: "Workz360", value: "Workz360" },
  { label: "Home Edition", value: "HomeEdition" },
];

interface ModalBusinessVerticalFormProps {
  open: boolean;
  onClose: () => void;
  data: BusinessVertical | null;
  defaultType?: BusinessVerticalType;
}

const ModalBusinessVerticalForm = ({
  open,
  onClose,
  data,
  defaultType = "Workz360",
}: ModalBusinessVerticalFormProps) => {
  const categoryType = defaultType === "HomeEdition" ? "home_edition" : "workz360";
  const [checked, setChecked] = React.useState<Map<number, boolean>>(new Map());
  const { data: allSkills, isLoading } = useGetSkillsQuery(
    {
      page: 0,
      pageSize: 1000,
      categoryType,
    },
    {
      skip: !open,
    }
  );
  const {
    data: initials,
    isLoading: loadingInitials,
    isFetching: fetchingInitials,
  } = useGetSkillsByVerticalQuery(
    { verticalId: data?.id! },
    { skip: !data, refetchOnMountOrArgChange: true }
  );
  const [edit, { isLoading: updating }] = useEditBusinessVerticalMutation();
  const [add, { isLoading: adding }] = useAddBusinessVerticalMutation();

  const {
    handleSubmit,
    clearErrors,
    control,
    setValue,
    formState: { errors },
  } = useForm();

  const categoryWithSkills = React.useMemo(() => {
    const tmp = new Map<number, Array<Skill & { category: SkillCategory; }>>();
    if (!allSkills) return tmp;

    allSkills.data.map((it) => {
      const category = tmp.get(it.category_id);
      if (!!category) {
        category.push(it);
      } else {
        tmp.set(it.category_id, [it]);
      }
    });
    return tmp;
  }, [allSkills]);

  React.useEffect(() => {
    const init = new Map();
    initials?.data.forEach((it) => init.set(it.id, true));
    setChecked(init);
  }, [initials]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const id = parseInt(event.target.name);
    setChecked(new Map(checked.set(id, event.target.checked)));
  };

  const onSubmit = async (payload: any) => {
    try {
      const selected = Array.from(checked)
        .filter((entry) => {
          const checked = entry[1];
          return checked;
        })
        .map((entry) => entry[0]);
      if (selected.length == 0) {
        toast.error("Please select at least one skill!");
        return;
      }
      const typeValue =
        typeof payload.type === "object" && payload.type?.value
          ? payload.type.value
          : payload.type || defaultType;
      const body = {
        name: payload.name,
        description: payload.description ?? "",
        type: typeValue as "Workz360" | "HomeEdition",
        skills: selected,
      };
      if (!data) {
        await add(body).unwrap();
      } else {
        await edit({ id: data.id, ...body }).unwrap();
      }
      onClose();
    } catch (err) {
      onClose();
    }
  };

  React.useEffect(() => {
    if (open) {
      setValue("name", data?.name || "");
      setValue("description", data?.description || "");
      setValue("type", data?.type || defaultType);
      if (!data) {
        setChecked(new Map());
      }
      clearErrors();
    }
  }, [data, open, setValue, clearErrors, defaultType]);

  return (
    <BaseModal
      open={open}
      onClose={onClose}
      title={`${!data ? "Create" : "Edit"} Industry`}
      maxWidth="sm"
      useGlassCard={false}
      footer={
        <>
          <Button variant="outlined" onClick={onClose}>
            Cancel
          </Button>
          <LoadingButton
            type="submit"
            variant="contained"
            loading={updating || adding}
            onClick={handleSubmit(onSubmit)}
          >
            {!data ? "Create" : "Update"}
          </LoadingButton>
        </>
      }
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Box sx={{ p: 3 }}>
          <Stack spacing={2}>
            <Controller
              name="name"
              control={control}
              rules={{ required: true }}
              render={({ field: { onChange, onBlur, value, ref } }) => (
                <StyledTextField
                  label="Industry name"
                  value={value || ""}
                  fullWidth
                  onChange={onChange}
                  onBlur={onBlur}
                  inputRef={ref}
                  error={!!errors.name}
                  helperText={!!errors.name && "This field is required"}
                />
              )}
            />
            <Controller
              name="type"
              control={control}
              render={({ field: { onChange, value } }) => {
                const raw = value?.value ?? value ?? defaultType;
                const selectedOption =
                  VERTICAL_TYPE_OPTIONS.find((o: SelectOption) => o.value === raw) ?? VERTICAL_TYPE_OPTIONS[0];
                return (
                  <SelectBase
                    label="Type"
                    options={VERTICAL_TYPE_OPTIONS}
                    value={selectedOption}
                    onChange={(v: SelectOption | SelectOption[] | null) => {
                      const option = Array.isArray(v) ? v[0] : v;
                      onChange(option && "value" in option ? option.value : option);
                    }}
                  />
                );
              }}
            />
            <Controller
              name="description"
              control={control}
              render={({ field: { onChange, onBlur, value, ref } }) => (
                <StyledTextField
                  label="Description (optional)"
                  value={value || ""}
                  fullWidth
                  onChange={onChange}
                  onBlur={onBlur}
                  inputRef={ref}
                  multiline
                  rows={3}
                />
              )}
            />
            <Box>
              {(isLoading || loadingInitials || fetchingInitials) && (
                <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              )}
              <Typography fontWeight={500} sx={{ mb: 2 }}>
                Select Skills
              </Typography>
              <Stack direction="column" spacing={2}>
                {Array.from(categoryWithSkills).map((entry) => {
                  let id = entry[0];
                  let skills = entry[1];

                  const categoryName = skills![0].category?.name || "?";
                  const getCheckboxes = () =>
                    skills.map((skill) => (
                      <FormControlLabel
                        key={skill.id}
                        sx={{ height: 32 }}
                        control={
                          <Checkbox
                            checked={checked.get(skill.id) || false}
                            onChange={handleChange}
                            name={skill.id.toString()}
                          />
                        }
                        label={skill.name}
                      />
                    ));
                  return (
                    <FormControl
                      key={id}
                      component="fieldset"
                      variant="standard"
                    >
                      <FormLabel component="legend">{categoryName}</FormLabel>
                      <FormGroup>{getCheckboxes()}</FormGroup>
                    </FormControl>
                  );
                })}
              </Stack>
            </Box>
          </Stack>
        </Box>
      </form>
    </BaseModal>
  );
};
export default ModalBusinessVerticalForm;
